import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TrendingUp, BarChart3, Menu, X, Search, Calendar, Calculator, BookOpen, Brain, ChevronDown, Settings, Eye, Building } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import { handleAffiliateClick } from "@/lib/affiliate";
import { useAuth } from "@/contexts/AuthContext";
import { useApexPromoStatus } from "@/hooks/useApexPromoStatus";

interface HeaderProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onSearch?: (query: string) => void;
}

const Header = ({ activeTab, onTabChange, onSearch }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { user, isAdmin, signOut } = useAuth();
  const { promo } = useApexPromoStatus();
  const navigate = useNavigate();

  // Direct navigation for routes that have dedicated pages
  const handleDirectNavigation = (tabId: string) => {
    const routes: Record<string, string> = {
      'blog': '/blog',
      'apex-review': '/apex-trader-funding-review',
    };
    if (routes[tabId]) {
      navigate(routes[tabId]);
    }
  };

  const handleSearch = () => {
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
      // Navigate to /blog for search (not SPA tab)
      window.location.href = `/blog?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  const mainTabs = [
    { id: "blog", label: "Blog", icon: BarChart3 },
    { id: "education", label: "Education", icon: BookOpen },
    { id: "journal", label: "Trading Journal", icon: BookOpen },
    { id: "apex-review", label: "Apex Review", icon: Building },
  ];

  const toolsTabs = [
    { id: "calculators", label: "Calculators", icon: Calculator },
    { id: "screener", label: "Screener", icon: Eye },
    { id: "charts", label: "Live Charts", icon: BarChart3 },
    { id: "calendar", label: "Economic Calendar", icon: Calendar },
    { id: "psychology", label: "Psychology", icon: Brain },
  ];

  const additionalTabs = [
    { id: "mentoring", label: "Mentoring", icon: Settings },
    ...(isAdmin ? [{ id: "admin", label: "Admin", icon: Settings }] : []),
  ];

  const allTabs = [...mainTabs, ...toolsTabs, ...additionalTabs];

  return (
    <header className="bg-card border-b border-border shadow-card">
      {/* Apex Promo Banner - shown throughout site */}
      <div 
        // min-h reserves the two-line mobile wrap so the hero never jumps when the promo text loads
        className="bg-trading-accent py-1.5 px-4 cursor-pointer hover:opacity-90 transition-opacity min-h-[66px] sm:min-h-[41px] flex items-center"
        onClick={async (e) => {
          await navigator.clipboard.writeText(promo.coupon_code || "Next-level");
          handleAffiliateClick(e);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="container mx-auto text-center w-full">
          <span className="text-xs sm:text-sm font-semibold text-background tracking-wide">
            {promo.is_active && promo.discount_percent
              ? `${promo.discount_percent}% OFF — ` 
              : ""}
            Use Apex Code <span className="underline decoration-background/60 underline-offset-2">&ldquo;{promo.coupon_code || "Next-level"}&rdquo;</span>{" "}
            — Get Funded Today!
          </span>
        </div>
      </div>
      
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Now clickable home button */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              onClick={() => onTabChange('home')}
              className="flex items-center space-x-2 p-2 hover:bg-transparent"
            >
              <TrendingUp className="h-8 w-8 text-trading-primary" />
              <span className="text-lg font-bold text-foreground hover:text-trading-primary transition-colors">
                Futures Trader Hub
              </span>
            </Button>
          </div>

          {/* Desktop Navigation - Cleaner layout */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Main tabs */}
            {mainTabs.map((tab) => {
              // Direct route navigation for blog and prop-firms
              if (tab.id === 'blog' || tab.id === 'apex-review') {
                return (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? "trading" : "ghost"}
                    onClick={() => handleDirectNavigation(tab.id)}
                    className="flex items-center space-x-1"
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </Button>
                );
              }
              return (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? "trading" : "ghost"}
                  onClick={() => onTabChange(tab.id)}
                  className="flex items-center space-x-1"
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </Button>
              );
            })}

            {/* Tools dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-1">
                  <Calculator className="h-4 w-4" />
                  <span>Tools</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {toolsTabs.map((tab) => (
                  <DropdownMenuItem
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Additional tabs */}
            {additionalTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? "trading" : "ghost"}
                onClick={() => onTabChange(tab.id)}
                className="flex items-center space-x-1"
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </Button>
            ))}
          </nav>

          {/* Compact Search & CTA */}
          <div className="hidden md:flex items-center space-x-3">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 pr-9 text-sm"
              />
              <Button
                onClick={handleSearch}
                size="sm"
                variant="ghost"
                aria-label="Search"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
            
            <Button 
              variant="affiliate" 
              size="sm"
              onClick={handleAffiliateClick}
              style={{ cursor: 'pointer' }}
              className="whitespace-nowrap"
            >
              Get Funded
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col space-y-2">
              {allTabs.map((tab) => {
                // Direct route navigation for blog and prop-firms in mobile menu
                if (tab.id === 'blog' || tab.id === 'apex-review') {
                  return (
                    <Button
                      key={tab.id}
                      variant={activeTab === tab.id ? "trading" : "ghost"}
                      onClick={() => {
                        handleDirectNavigation(tab.id);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-start space-x-2 w-full"
                    >
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </Button>
                  );
                }
                return (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? "trading" : "ghost"}
                    onClick={() => {
                      onTabChange(tab.id);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center justify-start space-x-2 w-full"
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </Button>
                );
              })}
              
              <div className="flex space-x-2 pt-2">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleSearch} size="sm" variant="ghost" aria-label="Search">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              
              <Button 
                variant="affiliate" 
                className="mt-4"
                onClick={handleAffiliateClick}
                style={{ cursor: 'pointer' }}
              >
                Get Funded with Apex
              </Button>
            </div>
          </div>
        )}

        {/* Newsletter Signup - Moved to a more subtle location */}
        <div className="hidden xl:block border-t border-border">
          <div className="py-1">
            <NewsletterSignup variant="header" />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;